import { defineView } from "eddev/views"
import { Header } from "@components/site/Header"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button, buttonStyles } from "@components/atoms/Button"
import { Appear } from "@components/motion/Appear"
import { useIntersectionObserver } from "usehooks-ts"
import { Link, useRouter } from "eddev/routing"
import { themes } from "@components/atoms/Themed"
import { cn } from "@utils/tw"
import { InsightTile } from "@components/atoms/InsightTile"
import { Select } from "@components/atoms/Select"

export default defineView("archive-insight", (props) => {
  const { isIntersecting, ref } = useIntersectionObserver({
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
    freezeOnceVisible: true,
  })
  const featured = props.featured?.nodes || []
  const featuredItem = featured.length > 0 ? featured[0] : props.insights?.nodes[0]
  const featuredThemeName = featuredItem?.insightHeader?.theme || "slateLight"
  const featuredTheme = themes[featuredThemeName as keyof typeof themes]
  const rest = props.insights?.nodes.filter((item) => item.id !== featuredItem?.id) || []
  const router = useRouter()

  return (
    <div className="w-full">
      <div className="w-full bg-slate200" ref={ref}>
        <Header noPattern barClassName="!bg-slate200" />
        <div className="relative w-full">
          <div className="content-sized pt-6 pb-8 flex flex-col">
            <div className="flex flex-col gap-6">
              <Appear as="h1" className="type-sans-3-xl-semibold" slide>
                Latest Insights
              </Appear>

              {featuredItem && (
                <Appear className="w-full grid grid-cols-1 md:grid-cols-2 gap-4" active={isIntersecting} activeOnly>
                  <Link
                    href={featuredItem.uri}
                    className="w-full rounded-md overflow-hidden bg-slate600 aspect-[520/390]"
                  >
                    {featuredItem.insightHeader?.featuredImage?.mediaItemUrl && (
                      <ResponsiveImage
                        {...featuredItem.insightHeader.featuredImage}
                        className="w-full h-full"
                        objectFit="cover"
                      />
                    )}
                  </Link>
                  <div
                    className={cn(
                      "w-full rounded-md bg-slate300 aspect-[520/390] flex justify-between p-5 flex-col gap-9 md:gap-0",
                      featuredTheme.bg,
                      featuredTheme.text,
                    )}
                  >
                    <div className="w-full flex flex-col gap-4 md:gap-2">
                      <div className="flex gap-4 items-center">
                        {featuredItem.insightHeader?.type?.insightType?.titleSingular && (
                          <Link
                            href={featuredItem.insightHeader.type.uri}
                            className="type-mono-xs-regular md:type-mono-sm-regular uppercase"
                          >
                            {featuredItem.insightHeader.type.insightType.titleSingular}
                          </Link>
                        )}
                        {featuredItem.insightHeader?.displayedDate && (
                          <p className="type-mono-xs-regular md:type-mono-sm-regular uppercase">
                            {featuredItem.insightHeader.displayedDate}
                          </p>
                        )}
                      </div>
                      <Appear delay={150} active={isIntersecting} activeOnly>
                        <Link href={featuredItem.uri} className="type-serif-2-xl-regular">
                          {featuredItem.title}
                        </Link>
                      </Appear>
                    </div>
                    <Appear delay={250} className="w-full" active={isIntersecting} activeOnly>
                      <Button
                        href={featuredItem.uri || ""}
                        variant={featuredTheme.button as keyof typeof buttonStyles.variants.variant}
                        size="sm"
                      >
                        Read More
                      </Button>
                    </Appear>
                  </div>
                </Appear>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content-sized my-8 flex flex-col gap-6">
        <div className="w-full flex gap-4">
          <Select
            options={[
              { label: "All Categories", value: "all" },
              ...(props.insightCategories?.nodes || []).map((type) => ({
                label: type.name || "",
                value: type.uri || "",
              })),
            ]}
            value={"all"}
            onChange={(value) => {
              // navigate to selected content type
              router.navigate(value === "all" ? "/insights" : value)
            }}
          />
          <Select
            options={[
              { label: "All Content Types", value: "all" },
              ...(props.insightTypes?.nodes || []).map((type) => ({ label: type.name || "", value: type.uri || "" })),
            ]}
            value="all"
            onChange={(value) => {
              // navigate to selected content type
              router.navigate(value === "all" ? "/insights" : value)
            }}
          />
        </div>
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          {rest.map((item, ix) => {
            const delay = ix * 150

            return (
              <InsightTile
                key={item.id}
                id={item.id}
                uri={item.uri || ""}
                theme={item.insightHeader?.theme || undefined}
                image={item.insightHeader?.featuredImage || undefined}
                title={item.title || ""}
                type={item.insightHeader?.type?.insightType?.titleSingular || undefined}
                typeUri={item.insightHeader?.type?.uri || undefined}
                date={item.insightHeader?.displayedDate || undefined}
                active={isIntersecting}
                delay={delay}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
})
